import { Link } from 'react-router-dom';
import { usePageMeta } from '../lib/usePageMeta';
import { trackEvent } from '../lib/analytics';
import { useSettings } from '../lib/useSettings';
import { ThankYou } from '../components/ThankYou';
import { Photo } from '../components/Mock';
import { HomeSections } from './home/HomeSections';
import { stayDate } from '../lib/format';
import { EVENT, SEO } from '../seo/pages';

/**
 * The venue comes from EVENT.venue in the SEO table, not from the settings row.
 *
 * Those two disagreed for a while on 2026-09-15: the code said Secrets Hideaway
 * and the database still said SpringHill Suites, so the page announced that the
 * celebration had moved to the hotel it had moved away from. A venue that ships
 * with the copy that explains it cannot drift out of step with it, and the
 * JSON-LD in the prerendered shell reads the same constant. The settings row
 * keeps the old hotel for the reservation records still being refunded.
 */
function eventDate(start: string | null): string | null {
  // One evening now, so only the start date is shown: a stale eventEndDate in
  // settings must not put a second day back on the page.
  return start ? stayDate(start) : null;
}

export function HomePage() {
  usePageMeta(SEO.home.title, SEO.home.description, { jsonLd: SEO.home.jsonLd });
  const { data: s } = useSettings();
  const when = eventDate(s?.eventStartDate ?? null);
  const venue = EVENT.venue.name;

  return (
    <>
      {/* Hero: Edem layout, yellow date, pink label, giant headline, blue CTA. */}
      <section className="bg-stage bg-hex relative overflow-hidden">
        <Photo
          name="hero.jpg"
          alt=""
          className="absolute inset-0 !bg-transparent opacity-60 [&>span]:hidden"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-night-950 via-night-950/70 to-transparent"
          aria-hidden
        />
        <span
          className="pointer-events-none absolute -right-24 top-24 hidden h-[520px] w-[520px] rotate-12 border-[14px] border-pink-500/80 lg:block"
          style={{ clipPath: 'polygon(50% 0, 100% 100%, 0 100%)' }}
          aria-hidden
        />
        {/*
          The hero owns the first screen: exactly one viewport tall (small
          viewport units so mobile browser chrome does not push it), content
          centred, nothing below it visible until the visitor scrolls. Short
          viewports get tighter padding and a headline capped by height so the
          buttons stay inside the fold.
        */}
        <div className="relative mx-auto flex min-h-svh max-w-6xl flex-col justify-center px-4 pb-16 pt-32 sm:pb-20 sm:pt-40 [@media(max-height:800px)]:pb-2 [@media(max-height:800px)]:pt-32 lg:[@media(max-height:800px)]:pt-24">
          <p className="text-meta text-xl sm:text-2xl [@media(max-height:800px)]:text-base">
            {when ?? 'Date coming soon'}
          </p>
          <p className="mt-3 [@media(max-height:800px)]:mt-2">
            <span className="label-highlight text-2xl sm:text-3xl [@media(max-height:800px)]:text-xl">
              Celebration of Life
            </span>
          </p>
          <h1 className="mt-6 text-[clamp(40px,10vh,60px)] leading-none sm:text-[clamp(48px,12vh,96px)] lg:text-[clamp(56px,12vh,110px)] [@media(max-height:800px)]:mt-2">
            Brandon
            <br />
            Taylor
          </h1>
          {/* The hashtag keeps its casing: text-meta uppercases, so normal-case wins here. */}
          <p className="text-meta mt-3 text-xl normal-case sm:text-2xl [@media(max-height:800px)]:mt-1 [@media(max-height:800px)]:text-base">
            #BrandonStrong
          </p>
          <div className="mt-6 max-w-xl [@media(max-height:800px)]:mt-4">
            <p className="text-lg text-ink-50 [@media(max-height:800px)]:text-base">
              One night, everyone who loves Brandon, at {venue}. Come in through the Tiki bar
              between seven and ten, free, and you will be given a wristband on the way in. There
              will be food, the night does not end at ten, and you should come dressed like a
              rockstar.
            </p>
            <p className="mt-3 text-base text-ink-200 [@media(max-height:800px)]:text-sm">
              Please RSVP with the names of your party. A donation is welcome, never required, and
              all of it goes to Shawna.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-4 [@media(max-height:800px)]:mt-4">
            <Link to="/rsvp" className="btn-primary">
              RSVP
            </Link>
            <Link to="/rooms" className="btn-outline">
              Where to stay
            </Link>
            {s?.donationUrl ? (
              <a
                href={s.donationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent"
                onClick={() => trackEvent('donate_click')}
              >
                Donate to the family
              </a>
            ) : (
              <Link to="/donate" className="btn-accent">
                Donate to the family
              </Link>
            )}
          </div>
        </div>
      </section>

      {/*
        The venue changed eighteen days out, so the first thing under the hero
        is the news, in the one colour the site can use as a surface under white
        text. It outranks everything else on the page until the night is over.
      */}
      <section className="border-y border-white/10 bg-pink-600">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:py-16">
          <p className="font-sans text-sm font-bold uppercase tracking-[0.02em] text-white/80">
            Please read
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl">The celebration has moved to {venue}</h2>
          <p className="mt-4 max-w-3xl text-lg text-white">
            The location changed for reasons outside the family&apos;s control. The date has not:
            entry is free from 7:00 to 10:00 PM through the Tiki bar, everyone is given a wristband
            on the way in, and the night carries on after ten. {venue} is sold out, so rooms are
            booked through SecretsOverflow, and every reservation made on this site is being
            refunded.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/update"
              className="btn-outline !border-white hover:!border-white hover:!text-white"
            >
              Read the full update
            </Link>
            <Link
              to="/rooms"
              className="btn-outline !border-white/60 hover:!border-white hover:!text-white"
            >
              Where to stay
            </Link>
          </div>
        </div>
      </section>

      <ThankYou />

      <HomeSections s={s} />
    </>
  );
}
